import { Layer, picking, project32 } from '@deck.gl/core';
import { IconLayer } from '@deck.gl/layers';
import type { Device } from '@luma.gl/core';
import { memoize } from 'lodash';
import type { RenderInfoBuffer } from 'src/lib/player/RenderInfoManager';
import { DEVICE_PIXEL_RATIO } from 'src/lib/player/constants';
import { hasMouse, make2DCanvas } from 'src/lib/player/utils';
import MarkerContext from './MarkerContext';
import { iconUniforms } from './vendor/icon-layer-uniforms';
import vs from './vendor/icon-layer-vertex.glsl';

const fs = `\
#version 300 es
#define SHADER_NAME icon-layer-fragment-shader

precision highp float;

uniform sampler2D iconsTexture;

in float vColorMode;
in vec4 vColor;
in vec2 vTextureCoords;
in vec2 uv;

out vec4 fragColor;

void main(void) {
  geometry.uv = uv;

  vec4 texColor = texture(iconsTexture, vTextureCoords);

  // Recolor texture with given color
  // This is the only change from the original shader
  vec3 color = texColor.rgb * vColor.rgb;
  // Take the global opacity and the alpha from vColor into account for the alpha component
  float a = texColor.a * layer.opacity * vColor.a;

  if (a < icon.alphaCutoff) {
    discard;
  }

  fragColor = vec4(color, a);
  DECKGL_FILTER_COLOR(fragColor, geometry);
}`;

const PARTICLE_MAPPING_ITEM = {
  x: 0,
  y: 0,
  width: 26 * DEVICE_PIXEL_RATIO,
  height: 26 * DEVICE_PIXEL_RATIO,
};

const getParticleDataUrl = memoize(function getParticleDataUrl(): string {
  const particleMeasurement = MarkerContext.measureParticle();
  const { canvas, context } = make2DCanvas(
    particleMeasurement.x * DEVICE_PIXEL_RATIO * 2,
    particleMeasurement.y * DEVICE_PIXEL_RATIO * 2,
    DEVICE_PIXEL_RATIO * 2,
  );
  MarkerContext.drawParticle(context, 1);
  return canvas.toDataURL();
});

class ParticleIconLayer extends IconLayer {
  static componentName = 'ParticleIconLayer';

  getShaders() {
    return Layer.prototype.getShaders.call(this, {
      vs,
      fs,
      modules: [project32, picking, iconUniforms],
    });
  }
}

type StarterParticleLayerProps = {
  renderInfoBuffer: RenderInfoBuffer;
  gpuDevice: Device;
  onClick: (index: number) => void;
  onHover: (index: number) => void;
};

export default function createStarterParticleLayer({
  renderInfoBuffer,
  gpuDevice,
  onClick,
  onHover,
}: StarterParticleLayerProps): IconLayer | null {
  if (gpuDevice == null) return null;

  const positions = renderInfoBuffer.positions;
  const colors = new Uint8ClampedArray(renderInfoBuffer.colors);
  for (let i = 0; i < renderInfoBuffer.length; i++) {
    if (!renderInfoBuffer.getShowParticle(i)) {
      colors[i * 4 + 3] = 0;
    }
  }

  // @ts-ignore: TS2554
  return new ParticleIconLayer({
    id: 'starter-particles',
    data: {
      length: renderInfoBuffer.length,
      attributes: {
        getPosition: { value: positions, size: 3, offset: 0, stride: 4 * 6 },
        getColor: { value: colors, size: 4 },
      },
    },
    billboard: true,
    pickable: true,
    getSize: 13,
    getPixelOffset: [0, 0],
    // @ts-expect-error: ts(2322)
    getIcon: 'particle',
    iconMapping: {
      'particle': PARTICLE_MAPPING_ITEM,
    },
    iconAtlas: getParticleDataUrl(),
    onClick: ({ index }: { index: number }) => {
      onClick(index);
    },
    onHover: hasMouse()
      ? ({ index }: { index: number }) => {
          onHover(index);
        }
      : null,
  });
}
